<script setup lang="ts">
import { AppTypography } from '@/components/base';
import { AppLogo } from '@/components/layout';
import { useLayoutStore } from '~/stores/layout';
import { MOBILE_MENU_NAV } from '~/utils/enums/navigation';

const layout = useLayoutStore();
</script>

<template>
  <!-- Full-page mobile menu (per Figma): sand overlay with the map at the
       top, own logo + close row, "Menu" heading and icon rows. -->
  <Transition name="app-sidebar">
    <aside
      v-show="layout.isSidebarActive"
      class="app-sidebar fixed inset-0 flex flex-col lg:hidden"
      data-testid="app-sidebar"
    >
      <div
        class="app-sidebar__topbar flex items-center justify-between"
        :style="{ height: 'var(--header-height)' }"
      >
        <AppLogo src="/logo-mobile.svg" width="129px" height="24px" />
        <button
          type="button"
          aria-label="Close menu"
          data-testid="sidebar-close"
          @click="layout.resetMenu()"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.75 0.75004L0.750061 10.75M0.750019 0.75L10.75 10.75"
              stroke="#201E1A"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <AppTypography type="title-5" element="h2" class="app-sidebar__heading">
        Menu
      </AppTypography>

      <nav class="flex flex-col">
        <NuxtLink
          v-for="item in MOBILE_MENU_NAV"
          :key="item.to"
          :to="item.to"
          class="app-sidebar__row flex items-center gap-4 no-underline"
          @click="layout.resetMenu()"
        >
          <Icon
            v-if="item.icon!.startsWith('solar:')"
            :name="item.icon!"
            class="text-xl"
          />
          <img v-else :src="item.icon" alt="" width="24" height="24" />
          <AppTypography type="body">{{ item.label }}</AppTypography>
        </NuxtLink>
      </nav>
    </aside>
  </Transition>
</template>

<style lang="scss" scoped>
// Hamburger open/close: slide the whole overlay in from the right with a
// fade. v-show + Transition (not v-if) keeps the nav mounted.
.app-sidebar-enter-active,
.app-sidebar-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.app-sidebar-enter-from,
.app-sidebar-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.app-sidebar {
  // Above the fixed topbar (z-1000) — the overlay owns the whole screen.
  z-index: 1100;
  padding: 0 $spacing-lg $spacing-lg;
  background-color: #ece3d1;
  overflow-y: auto;

  // Menu map accent: pre-cropped Figma export (375×136 frame, node
  // 40995:27524 "Header" > "BG") of the Port Phillip Bay / Mornington
  // Peninsula bend — not a manual crop of bg-map.png, so no positioning
  // math is needed; it's sized to its native aspect ratio and spans the
  // full sidebar width. z-index:-1 keeps it under the logo/rows.
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 36.2667vw; // 136 / 375 (native SVG aspect ratio)
    z-index: -1;
    background-image: url('/bg-map-mobile-menu.svg');
    background-repeat: no-repeat;
    background-size: 100% auto;
    pointer-events: none;
  }

  &__heading {
    margin: $spacing-xl 0 $spacing-md;
  }

  &__row {
    padding: $spacing-lg 0;
    border-bottom: 1px solid rgba(165, 138, 68, 0.35); // tan divider (Figma)

    // The global .router-link-active rule paints a light background; keep
    // rows flat on the sand overlay.
    &.router-link-active,
    &.router-link-exact-active {
      background: transparent;
    }
  }
}
</style>
